import React, { useRef, useState, useEffect } from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import HubIcon from '@mui/icons-material/Hub';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import type { SxProps } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { trackEvent } from './analytics';

function useScrollFade(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const fadeStyle = (inView: boolean, delay = 0): SxProps => ({
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(28px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
});

export default function GovernmentPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const whyRef = useRef<HTMLDivElement>(null);

  const whoSection = useScrollFade();
  const problemSection = useScrollFade();
  const stepsSection = useScrollFade();
  const evidenceSection = useScrollFade();
  const casesSection = useScrollFade();
  const ctaSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO
        title="Government & Ministries — Child Safety Intelligence for Regulators"
        description="SMCLA gives licensing boards, CPS agencies, and child welfare ministries real-time detection data, forensic-grade evidence, and audit-ready incident records — before a single complaint is filed."
        path="/government"
      />
      <Header />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <AccountBalanceIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Government & Regulators
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '4rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            Child safety intelligence<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>for those responsible for all of them.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 640, mx: 'auto', lineHeight: 1.65, mb: 6 }}>
            Licensing boards, child protective services, and ministries are called in after incidents occur. SMCLA shifts that — giving the agencies that oversee childcare facilities the evidence, patterns, and escalation pathways to act before a formal complaint is filed.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => { trackEvent('cta_click', 'Request Government Briefing', pathname); navigate('/contact'); }}
              sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Request a Government Briefing →
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => { trackEvent('cta_click', 'See Real Cases Gov', pathname); navigate('/why'); }}
              sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#9499b0', borderColor: 'rgba(148,153,176,0.3)', borderRadius: 2, '&:hover': { borderColor: '#9499b0', bgcolor: 'rgba(255,255,255,0.03)' } }}
            >
              See Real Cases →
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Who this serves ── */}
      <Box ref={whoSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(whoSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Who This Is For
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
            Built for every agency that oversees childcare.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            Whether you license facilities, investigate abuse reports, or set national child welfare policy — SMCLA gives your agency capabilities it doesn't currently have.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                accent: '#5ce1e6',
                icon: <VerifiedUserIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Childcare Licensing & Regulatory Agencies',
                body: 'Audit-ready incident logs for every facility on SMCLA. Every alert, every reviewer decision, every dismissal — permanently documented with timestamps and chain-of-custody metadata. Inspectors get a complete incident record, not just what staff self-reported.',
              },
              {
                accent: '#ff9f43',
                icon: <AssignmentLateIcon sx={{ color: '#ff9f43', fontSize: 22 }} />,
                title: 'Child Protective Services (CPS)',
                body: 'When a mandatory report is filed, the forensic clip package already exists. No reconstruction lag, no degraded footage, no he-said-she-said. Confirmed alerts are reviewed by a human before anything is shared — evidence arrives with chain-of-custody intact.',
              },
              {
                accent: '#8c52ff',
                icon: <AccountBalanceIcon sx={{ color: '#8c52ff', fontSize: 22 }} />,
                title: 'Ministries of Education & Social Services',
                body: 'Portfolio-level visibility across all licensed providers. Pattern detection identifies systemic risk across facilities — not just individual incidents. Ministries gain early warning data instead of post-incident damage control.',
              },
            ].map(({ accent, icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: `1px solid ${accent}20`, borderTop: `3px solid ${accent}`, borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'inline-flex', p: 1, bgcolor: `${accent}12`, borderRadius: 1.5, mb: 2 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1.5, lineHeight: 1.3 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── The problem government faces ── */}
      <Box ref={problemSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(problemSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Structural Problem
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Regulators inherit the aftermath.<br />
            <Box component="span" sx={{ color: '#ff9f43' }}>SMCLA makes incidents reportable in real time.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 6, maxWidth: 600 }}>
            Every regulatory system for childcare is built to respond to complaints. By the time an agency is involved, abuse has already occurred — sometimes for months. The data shows how large that gap is.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[
              {
                stat: '67 days',
                label: 'Blanding, Utah — time between first documented incident and arrest',
                sub: 'Cameras captured the abuse. Without real-time detection, it took over two months to act.',
                accent: '#ff6b6b',
              },
              {
                stat: '134',
                label: 'Documented incidents at one Texas facility before prosecution',
                sub: 'Three months of abuse against 6 toddlers. SMCLA would have flagged incident one.',
                accent: '#ff9f43',
              },
              {
                stat: '3 months',
                label: 'Typical gap between abuse onset and complaint reaching regulators',
                sub: 'Children cannot report. Parents don\'t know. Staff don\'t report colleagues. Cameras just record.',
                accent: '#8c52ff',
              },
            ].map(({ stat, label, sub, accent }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${accent}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: accent, lineHeight: 1, mb: 1 }}>{stat}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75, lineHeight: 1.35 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button variant="text" onClick={() => { trackEvent('cta_click', 'Read documented cases', pathname); navigate('/why'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#ff9f43', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              Read the documented cases →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── What SMCLA gives regulators ── */}
      <Box ref={stepsSection.ref}>
        <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(stepsSection.inView) }}>
          <Container maxWidth="md">
            <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
              Capabilities for Regulators
            </Typography>
            <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
              From reactive investigation to early warning.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
              SMCLA doesn't replace regulatory oversight — it gives it teeth. The evidence agencies need already exists by the time they're called.
            </Typography>

            <Stack spacing={2.5}>
              {[
                {
                  num: '1',
                  title: 'Real-time incident evidence — ready before you arrive',
                  body: 'When a mandatory report is filed, the forensic clip package already exists. Timestamped, tamper-evident, with full chain-of-custody metadata. No reconstruction from degraded footage, no reliance on staff accounts. The clip is court-ready the moment the report is made.',
                },
                {
                  num: '2',
                  title: 'Audit-ready incident history for every facility',
                  body: 'Every alert — whether validated, dismissed, or escalated — is permanently logged with reference key, timestamps, reviewer identity, and notes. Licensing inspectors get a complete, objective incident record for any facility on SMCLA. Not self-reported logs. Not incomplete incident forms.',
                },
                {
                  num: '3',
                  title: 'Pattern detection across facilities in your jurisdiction',
                  body: 'SMCLA identifies behavioral drift and repeated incident types before they reach the threshold of a criminal charge. Agencies gain early warning data on systemic risk across multiple facilities — not just post-incident documentation of a single event.',
                },
                {
                  num: '4',
                  title: 'Direct escalation pathways — built into every facility',
                  body: 'CPS, licensing boards, and law enforcement are named escalation targets in the SMCLA workflow. Confirmed alerts — reviewed by a human, not sent automatically — can be pushed directly to the right authority with the full evidence package attached. Human judgment gates every escalation.',
                },
              ].map(({ num, title, body }) => (
                <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' } }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* ── Evidence standards ── */}
      <Box ref={evidenceSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(evidenceSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Designed for Government Standards
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
            Privacy-by-design. Evidence-grade from day one.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            Government agencies have strict requirements for evidence handling and data privacy. SMCLA was built with these constraints as hard requirements, not afterthoughts.
          </Typography>

          <Grid container spacing={2.5}>
            {[
              {
                icon: <ContentPasteSearchIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Chain-of-custody metadata on every clip',
                body: 'Every flagged clip is timestamped, tamper-evident, and includes a complete access log — who viewed it, when, and what action they took. Meets evidentiary standards for licensing proceedings, family court, and criminal prosecution.',
              },
              {
                icon: <HubIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Snippet-only — never bulk surveillance',
                body: 'Only small flagged alert clips are ever transmitted or shared with authorities. Full footage streams never leave the facility. Governments do not receive surveillance data — only confirmed incident evidence.',
              },
              {
                icon: <LockPersonIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Role-gated access with full audit trail',
                body: 'Access to any clip requires authenticated, role-based authorization. Every view is logged with user identity and timestamp. Unauthorized access is impossible without leaving a permanent trace.',
              },
              {
                icon: <DeleteForeverIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: '48-hour retention, then permanently destroyed',
                body: 'Non-flagged footage is irrecoverably deleted after 48 hours — not archived, not analyzed, not retained. Only footage linked to an active investigation or audit hold is preserved, and only for as long as the case requires.',
              },
            ].map(({ icon, title, body }, i) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, height: '100%', transition: 'all 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.25)', transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(92,225,230,0.06)' }, ...fadeStyle(evidenceSection.inView, i * 0.08) }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'inline-flex', p: 1, bgcolor: 'rgba(92,225,230,0.08)', borderRadius: 1.5 }}>{icon}</Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>{title}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Real cases callout ── */}
      <Box ref={casesSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 10 }, px: 3, ...fadeStyle(casesSection.inView) }}>
        <Container maxWidth="md">
          <Box ref={whyRef} sx={{ p: { xs: 3, md: 5 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.15)', borderLeft: '3px solid #ff9f43', borderRadius: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff9f43', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>
              The cases that made this necessary
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', lineHeight: 1.25, mb: 2 }}>
              Every case in our database had cameras. None had real-time detection.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 3 }}>
              We've documented the incidents that regulatory systems failed to prevent — from 134-incident patterns that went undetected for months, to cases where cameras captured abuse that took 67 days to reach an arrest. These are the scenarios SMCLA is built to close.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => { trackEvent('cta_click', 'Read case record gov', pathname); navigate('/why'); }}
              sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#ff9f43', borderColor: 'rgba(255,159,67,0.4)', borderRadius: 2, px: 3, py: 1, '&:hover': { bgcolor: 'rgba(255,159,67,0.06)', borderColor: '#ff9f43' } }}
            >
              Read the case record →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box ref={ctaSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', ...fadeStyle(ctaSection.inView) }}>
        <Container maxWidth="sm">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            Government Briefings
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Request a briefing for your agency.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', lineHeight: 1.65, mb: 5 }}>
            We work directly with licensing boards, CPS agencies, and government procurement teams. Briefings are available for state, federal, and international agencies responsible for childcare oversight.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => { trackEvent('cta_click', 'Request Gov Briefing CTA', pathname); navigate('/contact'); }}
            sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Request a Government Briefing →
          </Button>
          <Typography sx={{ mt: 2, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.5)' }}>
            We respond to all government inquiries within one business day.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
